import spec from '../../shared/bias_lexicon.json'

export type BiasSurface =
  | 'job_brief'
  | 'job_description'
  | 'interview_guide'
  | 'live_interview'
  | 'competency_framework'
  | 'scorecard'
export type BiasSource = 'generate' | 'edit'

export type CoachingFinding = {
  id: string
  surface: BiasSurface
  phrase: string
  category: string
  category_label: string
  severity: string
  why: string
  coach: string
  alternative: string
  start: number
  end: number
  pattern_id: string
  /** Set on feedback-report rows; absent on a local `detect()` pass. */
  artefact_id?: string
}

export type BiasReport = {
  title: string
  lead: string
  recheck: string
  summary: string
  empty: string
  open: CoachingFinding[]
  open_count: number
  history: { id: string; source: BiasSource; finding_count: number }[]
  incident_count: number
  rewritten: false
  copy: typeof spec.copy
}

export const BIAS_COPY = spec.copy
export const BIAS_SURFACES = spec.surfaces as BiasSurface[]
export const BIAS_MODE = spec.mode
export const BIAS_SILENT_REWRITE = spec.silentRewrite

const CATEGORIES = spec.categories as Record<string, { label?: string; severity?: string }>

function compile(pattern: string, flags = 'i'): RegExp {
  return new RegExp(pattern, flags.includes('g') ? flags : `${flags}g`)
}

const PATTERNS = spec.patterns.map((row) => ({
  ...row,
  regex: compile(row.pattern, row.flags || 'i'),
}))

function overlaps(start: number, end: number, occupied: [number, number][]): boolean {
  return occupied.some(([left, right]) => start < right && end > left)
}

export function detect(text: string, surface: BiasSurface): CoachingFinding[] {
  if (!BIAS_SURFACES.includes(surface)) {
    throw new Error(`Unknown bias surface ${surface}`)
  }
  if (!text) return []
  const occupied: [number, number][] = []
  const findings: CoachingFinding[] = []
  for (const row of PATTERNS) {
    row.regex.lastIndex = 0
    let match: RegExpExecArray | null
    while ((match = row.regex.exec(text))) {
      const start = match.index
      const end = start + match[0].length
      if (overlaps(start, end, occupied)) continue
      occupied.push([start, end])
      const category = row.category
      const meta = CATEGORIES[category] || {}
      findings.push({
        id: `${surface}:${row.id}:${start}`,
        surface,
        phrase: match[0],
        category,
        category_label: meta.label || category.replace(/_/g, ' '),
        severity: row.severity || meta.severity || 'medium',
        why: row.why,
        coach: row.coach,
        alternative: row.alternative || '',
        start,
        end,
        pattern_id: row.id,
      })
    }
  }
  findings.sort((a, b) => a.start - b.start || a.end - b.end)
  return dedupeFindings(findings)
}

/**
 * One coaching note per pattern, earliest span first. Explanation stays on that hit.
 * Report rows span artefacts and surfaces, so the same phrase on two artefacts stays two notes.
 */
export function dedupeFindings<
  T extends { pattern_id: string; surface?: string; artefact_id?: string },
>(findings: T[]): T[] {
  const seen = new Set<string>()
  const unique: T[] = []
  for (const finding of findings) {
    const key = JSON.stringify([finding.surface ?? '', finding.artefact_id ?? '', finding.pattern_id])
    if (seen.has(key)) continue
    seen.add(key)
    unique.push(finding)
  }
  return unique
}

export function joinedText(...parts: Array<string | null | undefined>): string {
  return parts
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join('\n')
}

export function competencyFrameworkText(role: {
  title?: string | null
  summary?: string | null
  competencies?: Array<{
    rationale?: string | null
    source_excerpt?: string | null
    competency: {
      label?: string | null
      definition?: string | null
      levels?: Array<{ label?: string | null; anchor?: string | null }>
    }
  }>
}): string {
  const chunks: Array<string | null | undefined> = [role.title, role.summary]
  for (const row of role.competencies ?? []) {
    chunks.push(row.competency.label, row.competency.definition, row.rationale, row.source_excerpt)
    for (const level of row.competency.levels ?? []) {
      chunks.push(level.label, level.anchor)
    }
  }
  return joinedText(...chunks)
}

export function scorecardNotesText(input: {
  summary?: string | null
  ratings?: Array<{ notes?: string | null }>
}): string {
  return joinedText(input.summary, ...(input.ratings ?? []).map((row) => row.notes))
}

export function feedbackSummary(count: number): string {
  if (count <= 0) return BIAS_COPY.feedbackEmpty
  if (count === 1) return BIAS_COPY.feedbackSummaryOne
  return BIAS_COPY.feedbackSummaryMany.replace('{count}', String(count))
}
